import { useState } from 'react';
import { PermissionGuard } from '@/features/auth';
import { Button } from '@/shared/components';
import { handleUserMutationError } from '@/features/users/services/userErrorHandler';
import { useDealerContactMutations, useDealerContacts } from '../hooks/useDealerContacts';
import type { DealerContactFormInput } from '../schemas/dealerContact.schema';
import { DealerContactDataTable } from './DealerContactDataTable';
import { DealerContactForm } from './DealerContactForm';

interface DealerContactsProps {
  dealerId: string;
}

export function DealerContacts({ dealerId }: DealerContactsProps) {
  const [showForm, setShowForm] = useState(false);
  const { data, isLoading } = useDealerContacts(dealerId);
  const { createMutation, deleteMutation } = useDealerContactMutations(dealerId);

  const handleCreate = async (payload: DealerContactFormInput) => {
    try {
      await createMutation.mutateAsync({
        name: payload.name,
        role: payload.role || undefined,
        email: payload.email || undefined,
        phone: payload.phone || undefined,
        notify_agreements: payload.notify_agreements,
        notify_payments: payload.notify_payments,
      });
      setShowForm(false);
    } catch (error) {
      handleUserMutationError(error);
    }
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id, {
      onError: (error) => handleUserMutationError(error),
    });
  };

  return (
    <div className="space-y-6">
      <DealerContactDataTable
        data={data ?? []}
        isLoading={isLoading}
        onDelete={handleDelete}
        isDeleting={deleteMutation.isPending}
        emptyMessage="No contacts added for this dealer yet."
      />

      <div className="border-t border-mp-border pt-6 max-w-2xl">
        {showForm ? (
          <DealerContactForm
            onSubmit={handleCreate}
            onCancel={() => setShowForm(false)}
            isLoading={createMutation.isPending}
          />
        ) : (
          <div>
            <h3 className="font-semibold mb-2">Add New Contact</h3>
            <div className="flex items-start gap-4 justify-between">
              <p className="text-sm text-mp-muted">
                Add a contact for this dealer. Contacts can opt in to receive agreement or payment notifications.
              </p>
              <PermissionGuard permission={{ resource: 'dealer', action: 'edit' }}>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => setShowForm(true)}
                  className="whitespace-nowrap"
                >
                  Add New Contact
                </Button>
              </PermissionGuard>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
